import React, { useState, useMemo } from 'react'
import {
  Autocomplete,
  Box,
  BoxProps,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import invariant from 'tiny-invariant'
import { useFormik } from 'formik'
import { format } from 'date-fns'

import {
  ClinicalFocusArea,
  EditSteeragePayload,
  ElationReferralTemplate,
  GetElationLetterTemplatePayload,
  ClinicalFocusAreaAutocomplete,
  MEMBER_SERVICE_LEVEL,
  ReferralPriority,
  Service,
  Steerage,
  useEditSteerage,
  useGetElationLetterTemplate,
  useReferralDetails,
} from '~/entities/steerage'
import { physician, type PhysicianListResponse } from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { useGetPerson } from '~/entities/person'

import { NP_ROLE } from '~/shared/constants/careTeamRoles'
import { useCareTeam } from './useCareTeam'
import { isNoPartnersFoundError, NO_PARTNERS_FOUND_MESSAGE } from './utils'
import { queueNotification } from '~/shared/notifications/actions'
import PersonPreference from './PersonPreference'
import ServiceAutocomplete from './ServiceAutocomplete'
import { useDispatch } from 'react-redux'
type Physician = PhysicianListResponse[number]

const SEARCH_TYPES = {
  specialty: 'SpecialtyGroup' as const,
  facility: 'LocationType' as const,
} as const

type SearchType = (typeof SEARCH_TYPES)[keyof typeof SEARCH_TYPES]

const lastMileAnswer = (
  isLastMile: boolean | null | undefined,
  locationTypeLabel: string | null,
): null | string => {
  if (isLastMile === true) return 'YES'
  if (isLastMile === false) return 'NO'
  if (locationTypeLabel === 'Firefly Nearby') return 'YES'
  if (locationTypeLabel) return 'NO'
  return null
}

export const ReferralRequest: React.FC<{ steerage: Steerage }> = props => {
  const steerage = props.steerage

  // ALL HOOKS MUST BE CALLED FIRST - NO OTHER CODE BEFORE HOOKS
  const { isLoading: referralDetailsIsLoading, specialtiesAndLocationTypes } = useReferralDetails()
  const editSteerage = useEditSteerage()
  const getElationTemplate = useGetElationLetterTemplate()
  const { data: physicians, isPending: physiciansLoading } = useQuery(
    physician.listQueryKey(),
    async () => {
      const response = await physician.list()
      return [...response.data].sort((a, b) => {
        const firstNameComparison = a.first_name.localeCompare(b.first_name)
        if (firstNameComparison !== 0) {
          return firstNameComparison
        }
        return a.last_name.localeCompare(b.last_name)
      })
    },
    { staleTime: 5 * 60 * 1000 },
    { error: 'Failed to fetch physicians' },
  )

  // Get person data to extract user ID
  const { data: person, isPending: personLoading } = useGetPerson({
    personId: steerage?.person ?? 0,
  })
  const userId = person?.user ?? null

  // Get care team data using user ID (only when userId is available)
  // Since the userCareTeam query is disabled while there is no userID, we should be using the
  // the isFetching attribute here since fetching will return False when the query is disabled.
  // isLoading will return True even when the query is disabled since its effectively waiting for
  // the userID to be available.
  const { data: careTeam, isFetching: careTeamFetching } = useCareTeam(userId)

  // Find NP in care team and get physician information (memoized for performance)
  const npPhysician = useMemo(() => {
    if (!careTeam || !physicians) return null

    const npProvider = careTeam.find(provider => provider.group === NP_ROLE)
    return npProvider ? physicians.find(physician => physician.provider.pk === npProvider.id) : null
  }, [careTeam, physicians])

  const dispatch = useDispatch()
  const [searchTypeTab, setSearchTypeTab] = useState<SearchType>()
  const [collapse, setCollapse] = useState(!!steerage.is_locked)

  // The search tab defaults from the steerage's saved specialty or location type, and the panel
  // collapses with the lock; both follow the steerage when it changes.
  const defaultSearchTypeTab =
    !steerage?.specialty_group && steerage?.location_type
      ? SEARCH_TYPES.facility
      : SEARCH_TYPES.specialty
  const [searchTabDefaultedFor, setSearchTabDefaultedFor] = useState<string>()
  const searchTabSource = `${steerage?.specialty_group}:${steerage?.location_type}`
  if (searchTabSource !== searchTabDefaultedFor) {
    setSearchTabDefaultedFor(searchTabSource)
    setSearchTypeTab(defaultSearchTypeTab)
  }
  const [collapsedForLocked, setCollapsedForLocked] = useState(steerage.is_locked)
  if (steerage.is_locked !== collapsedForLocked) {
    setCollapsedForLocked(steerage.is_locked)
    setCollapse(!!steerage.is_locked)
  }

  // Calculate initial values for formik - moved before formik to avoid hook order issues
  const initialSpecialtyGroup = steerage?.specialty_group ?? 0
  const initialLocationType = steerage?.location_type
  const selectedInitialSpecialtyGroup =
    specialtiesAndLocationTypes.find(
      s => s.id === initialSpecialtyGroup && s?.source === 'SpecialtyGroup',
    ) ?? null
  const selectedInitialLocationType =
    specialtiesAndLocationTypes.find(
      s => s.id === initialLocationType && s?.source === 'LocationType',
    ) ?? null
  const initialSpecialtyGroupLabel = selectedInitialSpecialtyGroup?.label ?? null
  const initialLocationTypeLabel = selectedInitialLocationType?.label ?? null
  // Prefer the persisted answer over deriving one from the location label. A segmented steerage
  // renders its autocompletes disabled, so the mount-time onInputChange that would otherwise
  // supply this value never fires — and the send button reads it to decide whether it is enabled.
  const initialIsLastMile = lastMileAnswer(
    steerage?.referral?.is_last_mile,
    initialLocationTypeLabel,
  )
  const initialNotifyClinicianYn: null | string =
    steerage?.notify_clinician_yn === true ? 'YES' : null

  // Calculate default ordering physician: use existing value or NP from care team
  const defaultOrderingPhysician = steerage?.referral?.ordering_physician ?? npPhysician ?? null

  // Annotate so the empty-string sentinel doesn't widen the field to `string`;
  // keeping the generated enum lets the submit payload stay typed without a cast.
  const initialPriority: EditSteeragePayload['priority'] | '' = steerage?.priority ?? ''

  // Formik deep-compares these, so rebuilding them each render reinitialises only on a real change.
  const initialValues = {
    preferredProviderOrFacility: steerage?.preferred_provider_or_facility ?? '',
    specialtyGroup: initialSpecialtyGroupLabel,
    locationType: initialLocationTypeLabel,
    priority: initialPriority,
    typeOfVisit: steerage?.type_of_visit ?? 'Any',
    isLastMile: initialIsLastMile,
    // The form edits these as the hand-written `Service`/`ClinicalFocusArea` shapes
    // (also sourced from specialty/location options); map the snake_case steerage
    // payload onto that shape at this boundary.
    services: (steerage?.services ?? []).map(service => ({
      id: service.id,
      description: service.description ?? '',
      category: {
        id: service.category.id,
        description: service.category.description ?? '',
        ordering: service.category.ordering ?? 0,
      },
    })),
    clinicalFocusAreas: (steerage?.clinical_focus_areas ?? []).map(area => ({
      id: area.id,
      label: area.label,
    })),

    notifyClinicianYn: initialNotifyClinicianYn,
    sendToElation: false,
    referralBody: steerage?.referral?.body ?? '',
    orderingPhysician: defaultOrderingPhysician,
    isReferralNotActive:
      steerage?.referral?.is_active != null
        ? !steerage?.referral?.is_active
        : steerage?.segment
          ? true
          : false,
  }
  const formik = useFormik({
    // This allows formik to reinitialize when steerage or dependencies change
    enableReinitialize: true,
    initialValues,
    onSubmit: values => {
      // Coerce the isLastMile value to a boolean
      let isLastMile: boolean | null = null
      if (values.isLastMile === 'YES') isLastMile = true
      else if (values.isLastMile === 'NO') isLastMile = false

      // Coerce the notifyClinicianYn value to a boolean
      let notifyClinicianYn: boolean | null = null
      if (values.notifyClinicianYn === 'YES') notifyClinicianYn = true
      else if (values.notifyClinicianYn === 'NO') notifyClinicianYn = false

      // Fetch the ServiceIds
      let serviceIds: number[] = []
      serviceIds = values.services.map(service => service.id)

      // If there are no services selected, it may be because the user selected a specialty that has only one service.
      // In that case, we need to select that service
      if (values.services.length === 0) {
        if (values?.specialtyGroup) {
          const specialty = specialtyGroupsOptions.find(s => s.label === values.specialtyGroup)
          const services = specialty?.services ?? []
          const [onlyService] = services
          if (services.length === 1 && onlyService) {
            serviceIds = [onlyService.id]
          }
        } else {
          const location_type = locationTypesOptions.find(s => s.label === values.locationType)
          const services = location_type?.services ?? []
          const [onlyService] = services
          if (services.length === 1 && onlyService) {
            serviceIds = [onlyService.id]
          }
        }
      }

      // Fetch the Clinical Focus Area IDs
      const clinicalFocusAreaIds = values.clinicalFocusAreas.map(
        clinicalFocusArea => clinicalFocusArea.id,
      )

      const payload: EditSteeragePayload = {
        id: steerage.id,
        priority: values.priority || null,
        type_of_visit: virtualAppropriateBasedOnSpecialty == true ? values.typeOfVisit : 'Any',
        specialty_group: null, // these should be set in the if condition below, null is the default to clear
        location_type: null,
        service_ids: serviceIds,
        clinical_focus_area_ids: clinicalFocusAreaIds,
      }

      payload['preferred_provider_or_facility'] = values.preferredProviderOrFacility
      if (!shouldHideReferral) {
        payload['notify_clinician_yn'] = notifyClinicianYn
        payload['referral'] = {
          is_last_mile: isLastMile,
          body: values.referralBody,
          is_active: !values.isReferralNotActive,
        }
        if (payload['referral']) {
          const orderingPhysicianId = values.orderingPhysician ? values.orderingPhysician.id : null
          if (orderingPhysicianId)
            payload['referral']['ordering_physician_id'] = orderingPhysicianId
        }
        payload['send_to_elation'] = values?.sendToElation
      }
      if (values.specialtyGroup) {
        const chosenSpecialty = specialtyGroupsOptions.find(s => s.label === values.specialtyGroup)
        payload['specialty_group'] = chosenSpecialty?.id
        invariant(chosenSpecialty, 'Chosen specialty not found')
      } else {
        const locationType = locationTypesOptions.find(s => s.label === values.locationType)
        payload['location_type'] = locationType?.id
        invariant(locationType, 'Chosen service type not found')
      }

      // Update the API and reset the form values
      editSteerage.mutate(payload, {
        // Use new initial values to reset the form
        onSuccess: () => formik.resetForm(),
        onError: (error: unknown) => {
          if (!isNoPartnersFoundError(error)) return
          // This will happen when a location type has no fallback mechanism. We will need to clear out the steerage.
          formik.resetForm()
          void formik.setFieldValue('locationType', '')
          // Show toast to suggest selecting another specialty
          dispatch(
            queueNotification({
              message: NO_PARTNERS_FOUND_MESSAGE,
              variant: 'error',
            }),
          )
        },
      })
    },
  })

  // Derived values used throughout the component
  const isLoading =
    referralDetailsIsLoading ||
    editSteerage.isPending ||
    physiciansLoading ||
    personLoading ||
    careTeamFetching
  const isLocked = !!steerage.is_locked

  // Helper to get physician display name.
  const getPhysicianDisplayName = (physician: Physician) => {
    const { first_name: firstName, last_name: lastName, title } = physician
    return `${firstName} ${lastName}${title ? `, ${title}` : ''}`
  }

  let virtualAppropriateBasedOnSpecialty =
    selectedInitialSpecialtyGroup?.does_virtual_partner_exist ||
    selectedInitialLocationType?.does_virtual_partner_exist

  /**
   * There are two scenarios for showing the referral section to the ops
   * 1. If member is switched from (care or Care+Coverage) to Coverage only then we need to show the past referral details
   * 2. For new referral
   * For the 1st case where member switched the program, we need to add extra condition where referral is present to ensure
   * that ops can see the past referral details
   */
  const shouldHideReferral =
    steerage?.member_service_level == MEMBER_SERVICE_LEVEL.COVERAGE &&
    steerage?.referral?.is_active != true

  const handleCreateReferralTemplateClick = () => {
    if (steerage?.person) {
      let isLastMile: boolean | undefined
      if (formik.values.isLastMile === 'YES') isLastMile = true
      else if (formik.values.isLastMile === 'NO') isLastMile = false

      const payload: GetElationLetterTemplatePayload = {
        person_id: steerage?.person,
        is_last_mile: isLastMile,
        priority: formik.values.priority ?? undefined,
      }

      if (isLastMile) {
        const serviceDescriptions = formik.values.services
          .map(service => service.description)
          .toString()
        payload['services'] = serviceDescriptions
      }

      if (formik.values.orderingPhysician) {
        const orderingPhysicianId = formik.values.orderingPhysician.id ?? null
        if (orderingPhysicianId) payload['ordering_physician_id'] = orderingPhysicianId
      }

      getElationTemplate.mutate(payload, {
        onSuccess: (response: ElationReferralTemplate) => {
          void formik.setFieldValue('referralBody', response.body)
        },
      })
    }
  }

  /**
   * Start Form States
   */
  const isDisabled =
    isLoading ||
    Boolean(steerage.is_locked) ||
    Boolean(steerage.was_elation_letter_created_from_lucian) ||
    steerage.segment !== null

  /**
   * When this component is not in read-only mode, we need to make sure that deprecated
   * Specialty Groups are not used. Read-only Steerages may still reference them.
   */
  let specialtyGroupsOptions = specialtiesAndLocationTypes.filter(
    s => s.source === 'SpecialtyGroup',
  )
  let locationTypesOptions = specialtiesAndLocationTypes.filter(s => s.source === 'LocationType')

  if (!isDisabled) {
    specialtyGroupsOptions = specialtyGroupsOptions.filter(option => {
      return !option.is_deprecated && option.source === searchTypeTab
    })
    locationTypesOptions = locationTypesOptions.filter(option => {
      return !option.is_deprecated && option.source === searchTypeTab
    })
  }

  /**
   * This is the list of services based on the selected specialty
   */
  const servicesBasedOnSpecialty = (() => {
    if (formik.values.specialtyGroup) {
      return (
        specialtyGroupsOptions.find(s => s.label === formik.values.specialtyGroup)?.services ?? []
      )
    } else if (formik.values.locationType) {
      return locationTypesOptions.find(s => s.label === formik.values.locationType)?.services ?? []
    } else {
      return []
    }
  })()

  /**
   * This is the list of clinical focus areas based on the selected specialty
   */
  const clinicalFocusAreasBasedOnSpecialty = (() => {
    if (formik.values.specialtyGroup) {
      return (
        specialtyGroupsOptions.find(s => s.label === formik.values.specialtyGroup)
          ?.clinical_focus_areas ?? []
      )
    } else if (formik.values.locationType) {
      return (
        locationTypesOptions.find(s => s.label === formik.values.locationType)
          ?.clinical_focus_areas ?? []
      )
    } else {
      return []
    }
  })()

  /**
   * Separate "locked" uneditable state from potential loading spinner type state
   * It's appropriate to disable inputs for loading state, but not appropriate to
   * reorganize grid column sizing based on it
   */
  const isSendToElationDisabled =
    (!formik.values?.specialtyGroup && !formik.values?.locationType) ||
    !formik.values?.priority ||
    (!formik.values?.typeOfVisit && virtualAppropriateBasedOnSpecialty == true) ||
    !formik.values?.isLastMile ||
    (formik.values.services.length == 0 &&
      servicesBasedOnSpecialty &&
      servicesBasedOnSpecialty.length > 1)

  // For saving referral request section, we would need to have the specialtyGroup, priority and services present
  const isSaveButtonDisabled =
    (!formik.values?.specialtyGroup && !formik.values?.locationType) ||
    !formik.values?.priority ||
    (formik.values.services.length == 0 &&
      servicesBasedOnSpecialty &&
      servicesBasedOnSpecialty.length > 1)

  // Render the readonly referral if the body exists *or* it was generated in Elation
  const shouldRenderReferralTemplate =
    Boolean(steerage?.referral?.body) || steerage.is_initiated_from_lucian === false
  // Allow generating a referral template if the referral was *not* generated in Elation
  const shouldRenderGenerateReferralTemplate =
    Boolean(steerage.is_initiated_from_lucian) && steerage.segment === null

  const shouldRenderSaveButton =
    (!steerage.is_initiated_from_lucian ||
      shouldHideReferral ||
      formik.values.isReferralNotActive) &&
    steerage.segment === null

  // Segmenting a steerage normally retires this button. A failed letter creation is the one case
  // where it comes back, because segmentation happens on the same save that failed and ops would
  // otherwise have no way to retry.
  const shouldRenderSaveAndSendToElationButton =
    Boolean(
      steerage.is_initiated_from_lucian &&
      !shouldHideReferral &&
      !formik.values.isReferralNotActive,
    ) &&
    (steerage.segment === null || steerage?.referral_letter_creation_status === 'failed')

  const isReferralActive = steerage?.referral?.is_active == true

  // Only render the clinical coordination details referral is active
  const renderClinicalCoordination =
    (!formik.values?.isReferralNotActive && !steerage?.segment) ||
    (steerage?.segment && steerage?.referral?.is_active)
  /**
   * End Form States
   */

  /**
   * Collapsible state for the form
   * Keep separate state for collapsing section, but defer to isLocked state so it
   * expands if the steerage is unlocked
   */
  const shouldCollapse = !collapse || !isLocked
  let collapseChipLabel: string | null = null
  let collapseChipColor: 'success' | 'info' | 'error' = 'success'
  const sentDate = steerage?.referral?.vendor_create_date
  const letterCreationStatus = steerage?.referral_letter_creation_status
  if (sentDate) {
    collapseChipLabel = `Sent to Elation on ${format(
      new Date(sentDate),
      'MMM d, yyyy',
    )} at ${format(new Date(sentDate), 'h:mmaaa')}`
  } else if (letterCreationStatus === 'queued') {
    // `created` deliberately shows nothing until the Elation webhook supplies vendor_create_date,
    // so the dated message stays the single "it landed" signal.
    collapseChipLabel = 'Sending to Elation'
    collapseChipColor = 'info'
  } else if (letterCreationStatus === 'failed') {
    collapseChipLabel = 'Send to Elation failed'
    collapseChipColor = 'error'
  }

  // Based on specialty value, decide when to show or hide the virtual appropriate dropdown
  if (formik.values.specialtyGroup) {
    const specialtyGroup = specialtyGroupsOptions.find(
      s => s.label === formik.values.specialtyGroup,
    )
    virtualAppropriateBasedOnSpecialty = specialtyGroup
      ? specialtyGroup?.does_virtual_partner_exist
      : false
  } else {
    const locationType = locationTypesOptions.find(s => s.label === formik.values.locationType)
    virtualAppropriateBasedOnSpecialty = locationType
      ? locationType?.does_virtual_partner_exist
      : false
  }

  return (
    <Box sx={{ my: 1, mx: 1 }}>
      <form
        onSubmit={e => {
          formik.handleSubmit()
          // Prevent the page from refreshing
          e.preventDefault()
        }}
      >
        <Box>
          {steerage?.person && (
            <Grid size={12}>
              <PersonPreference personId={steerage.person} showTitle={false} />
            </Grid>
          )}
        </Box>
        <BackgroundBlock>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Grid sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {isLocked ? (
                <IconButton edge="start" onClick={() => setCollapse(c => !c)}>
                  {shouldCollapse ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </IconButton>
              ) : null}
              <Typography sx={{ fontSize: 16, fontWeight: '500' }}>Request</Typography>
            </Grid>
          </Box>
          <Collapse in={shouldCollapse}>
            <Grid container columnSpacing={2} rowSpacing={isLocked ? 1 : 3} sx={{ mb: 1 }}>
              <Grid size={12} sx={{ mt: 1 }}>
                <Box sx={{ alignSelf: 'center' }}>
                  <ToggleButtonGroup
                    color="primary"
                    value={searchTypeTab}
                    exclusive
                    onChange={(_, nextTab: SearchType | null) => {
                      // `exclusive` reports null when the selected toggle is clicked again.
                      // Clearing the search type here would leave neither branch selected and
                      // discard the fields below with it, so a deselect is ignored.
                      if (!nextTab) return
                      setSearchTypeTab(nextTab)
                      void formik.setFieldValue('specialtyGroup', '')
                      void formik.setFieldValue('locationType', '')
                      void formik.setFieldValue('services', [])
                      void formik.setFieldValue('clinicalFocusAreas', [])
                    }}
                    size="small"
                    aria-label="Status"
                    sx={{
                      '.MuiToggleButton-root.MuiToggleButtonGroup-grouped': {
                        padding: '7px 20px',
                        textTransform: 'capitalize',
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '.MuiToggleButton-root.Mui-selected': {
                        backgroundColor: 'tones.neutral.bg',
                        color: 'text.primary',
                      },
                    }}
                  >
                    <ToggleButton value={SEARCH_TYPES.specialty} disabled={isDisabled}>
                      Provider
                    </ToggleButton>
                    <ToggleButton value={SEARCH_TYPES.facility} disabled={isDisabled}>
                      Service
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              <Grid size={6}>
                {searchTypeTab === SEARCH_TYPES.facility ? (
                  <Autocomplete
                    disabled={isDisabled}
                    onInputChange={(_, val) => {
                      // Only derive state if the location type actually changed: this handler
                      // also fires on mount, where it would overwrite the persisted answers.
                      if (val !== formik.values.locationType) {
                        if (val === 'Urgent Care') {
                          void formik.setFieldValue('isLastMile', 'YES')
                        } else if (val) {
                          void formik.setFieldValue('isLastMile', 'NO')
                        } else {
                          void formik.setFieldValue('isLastMile', null)
                        }

                        void formik.setFieldValue('services', [])
                      }

                      void formik.setFieldValue('locationType', val)
                    }}
                    onChange={formik.handleChange}
                    value={formik.values.locationType}
                    options={locationTypesOptions}
                    freeSolo
                    renderInput={params => (
                      <TextField required variant="standard" {...params} label="Service Type" />
                    )}
                  />
                ) : (
                  <Autocomplete
                    disabled={isDisabled}
                    onInputChange={(_, val) => {
                      // Only derive state if the specialty actually changed: this handler also
                      // fires on mount, where it would overwrite the persisted answers.
                      if (val !== formik.values.specialtyGroup) {
                        void formik.setFieldValue('isLastMile', 'NO')
                        void formik.setFieldValue('services', [])
                      }
                      void formik.setFieldValue('specialtyGroup', val)
                    }}
                    onChange={formik.handleChange}
                    value={formik.values.specialtyGroup}
                    options={specialtyGroupsOptions}
                    freeSolo
                    renderInput={params => (
                      <TextField required variant="standard" {...params} label="Specialty" />
                    )}
                  />
                )}
              </Grid>
              {/* Only show the servicesBasedOnSpecialty if the length is greater then one as we will auto select the only service*/}
              {/* If length is 1, just show the service category */}
              {servicesBasedOnSpecialty.length > 1 || formik.values.services.length > 0 ? (
                <Grid size={6}>
                  <ServiceAutocomplete
                    disabled={isDisabled}
                    services={servicesBasedOnSpecialty}
                    onChangeService={value => {
                      // This onChange handler emits the selected values *by category*
                      // We need to reassemble the list by ID
                      const newServices: Service[] = []
                      value.forEach(v => {
                        const found = servicesBasedOnSpecialty.find(s => s.id === v.id)
                        if (found) newServices.push(found)
                      })
                      void formik.setFieldValue('services', newServices)
                    }}
                    selectedServices={formik.values.services}
                  />
                  {formik.errors.services ? (
                    <FormHelperText error>{formik.errors.services as string}</FormHelperText>
                  ) : null}
                </Grid>
              ) : // Since a service won't be added here, we just show Consultation for the time being till the service
              // gets added in the backend
              servicesBasedOnSpecialty.length === 1 ? (
                <Grid size={6}>
                  <TextField
                    disabled={true}
                    fullWidth
                    label="Service"
                    value={servicesBasedOnSpecialty[0]?.category?.description}
                    variant="standard"
                  />
                </Grid>
              ) : (
                <Grid size={6}></Grid>
              )}
              {clinicalFocusAreasBasedOnSpecialty.length > 0 ? (
                <Grid size={6}>
                  <ClinicalFocusAreaAutocomplete
                    disabled={isDisabled}
                    clinicalFocusAreas={clinicalFocusAreasBasedOnSpecialty}
                    onChangeClinicalFocusArea={value => {
                      // This onChange handler emits the selected values *by category*
                      // We need to reassemble the list by ID
                      const newClinicalFocusAreas: ClinicalFocusArea[] = []
                      value.forEach(v => {
                        const found = clinicalFocusAreasBasedOnSpecialty.find(s => s.id === v.id)
                        if (found) newClinicalFocusAreas.push(found)
                      })
                      void formik.setFieldValue('clinicalFocusAreas', newClinicalFocusAreas)
                    }}
                    selectedClinicalFocusAreas={formik.values.clinicalFocusAreas}
                  />
                  {formik.errors.services ? (
                    <FormHelperText error>{formik.errors.services as string}</FormHelperText>
                  ) : null}
                </Grid>
              ) : null}

              <Grid size={6}>
                <FormControl fullWidth variant="standard">
                  <InputLabel disabled={isDisabled} required>
                    Priority
                  </InputLabel>
                  <Select
                    label="Priority"
                    name="priority"
                    value={formik.values.priority}
                    onChange={e => {
                      void formik.setFieldValue('priority', e.target.value)
                      void formik.setFieldTouched('priority')
                    }}
                    variant="standard"
                    disabled={isDisabled}
                    fullWidth
                    required
                  >
                    {Object.entries(ReferralPriority).map(([priorityKey, label]) => (
                      <MenuItem key={priorityKey} value={priorityKey}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <TextField
                  disabled={isDisabled}
                  fullWidth
                  label="Preferred partner or provider"
                  name="preferredProviderOrFacility"
                  onChange={formik.handleChange}
                  value={formik.values.preferredProviderOrFacility}
                  variant="standard"
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                />
              </Grid>
              {virtualAppropriateBasedOnSpecialty ? (
                <Grid size={6} sx={{ mt: 1 }}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel disabled={isDisabled} required>
                      Virtual appropriate
                    </InputLabel>
                    <Select
                      label="Virtual appropriate"
                      name="typeOfVisit"
                      onChange={e => {
                        void formik.setFieldValue('typeOfVisit', e.target.value)
                      }}
                      value={formik.values.typeOfVisit}
                      variant="standard"
                      disabled={isDisabled}
                      fullWidth
                      required
                    >
                      <MenuItem value="Any">Yes</MenuItem>
                      <MenuItem value="In Person">No</MenuItem>
                      {/**
                       * The following items are deprecated, but necessary in order to render values from legacy data.
                       * If the API-side choices are removed and a backfill is done, these can be removed.
                       */}
                      <MenuItem sx={{ display: 'none' }} disabled value="Virtual">
                        Virtual
                      </MenuItem>
                      <MenuItem sx={{ display: 'none' }} disabled value="In Person Or Virtual">
                        In Person Or Virtual
                      </MenuItem>
                      <MenuItem sx={{ display: 'none' }} disabled value="Home">
                        In Home
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <Grid size={6}></Grid>
              )}
            </Grid>
          </Collapse>
        </BackgroundBlock>
        {!shouldHideReferral && (
          <BackgroundBlock>
            <Box sx={{ display: 'flex' }}>
              <Grid size={6} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: '500' }}>Clinical Referral</Typography>
              </Grid>
              <Grid size={6} sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
                {isReferralActive ? (
                  collapseChipLabel && (
                    <Chip color={collapseChipColor} size="small" label={collapseChipLabel} />
                  )
                ) : steerage?.member_service_level != MEMBER_SERVICE_LEVEL.CARE ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.isReferralNotActive}
                        name="isReferralNotActive"
                        onChange={formik.handleChange}
                        disabled={isDisabled}
                      />
                    }
                    label="Clinical coordination is not needed"
                  />
                ) : null}
              </Grid>
            </Box>

            {renderClinicalCoordination ? (
              <>
                <Grid container columnSpacing={2} rowSpacing={isLocked ? 1 : 3}>
                  <Grid size={6} sx={{ mt: 1 }}>
                    <Autocomplete
                      disabled={isDisabled || physiciansLoading}
                      onChange={(_, value) => {
                        void formik.setFieldValue('orderingPhysician', value || null)
                        void formik.setFieldTouched('orderingPhysician')
                      }}
                      value={formik.values.orderingPhysician ?? null}
                      options={physicians ?? []}
                      // `freeSolo` means an option can also be the raw text the user typed.
                      getOptionLabel={option =>
                        typeof option === 'string' ? option : getPhysicianDisplayName(option)
                      }
                      isOptionEqualToValue={(option, value) =>
                        typeof option === 'string' || typeof value === 'string'
                          ? option === value
                          : option.id === value?.id
                      }
                      freeSolo
                      renderInput={params => (
                        <TextField
                          variant="standard"
                          {...params}
                          label="Ordering Clinician"
                          error={!!formik.errors.orderingPhysician}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={6} sx={{ mt: 1 }}>
                    <FormControl fullWidth variant="standard">
                      <InputLabel disabled={isDisabled} required>
                        Notify clinician if the member doesn&apos;t schedule
                      </InputLabel>
                      <Select
                        error={!!formik.errors.notifyClinicianYn}
                        disabled={isDisabled}
                        value={formik.values.notifyClinicianYn}
                        name="notifyClinicianYn"
                        onChange={e => {
                          void formik.setFieldValue('notifyClinicianYn', e.target.value)
                        }}
                        label="Notify clinician if the member doesn't schedule"
                        variant="standard"
                        fullWidth
                        required
                      >
                        <MenuItem value="YES">Yes</MenuItem>
                        <MenuItem value="NO">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {shouldRenderReferralTemplate ? (
                  <>
                    <Grid size={12} sx={{ mt: 1 }}>
                      {steerage?.referral?.body?.length ? (
                        <Grid size={12} sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: '500' }}>Referral body</Typography>
                          <Typography sx={{ whiteSpace: 'break-spaces' }}>
                            {steerage?.referral?.body}
                          </Typography>
                        </Grid>
                      ) : (
                        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No referral body entered in Elation
                        </Typography>
                      )}
                    </Grid>
                  </>
                ) : shouldRenderGenerateReferralTemplate ? (
                  <>
                    <Grid
                      size={12}
                      sx={{ ml: -1, mt: 1, mb: 1, display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <Button
                        name="createReferralTemplate"
                        disabled={isLoading || isSendToElationDisabled}
                        onClick={handleCreateReferralTemplateClick}
                        endIcon={
                          getElationTemplate.isPending ? (
                            <CircularProgress size="1em" color="inherit" />
                          ) : null
                        }
                      >
                        <Chip
                          icon={<AutoAwesomeIcon />}
                          label={'Generate Referral Template'}
                          variant="outlined"
                          size="small"
                          sx={{
                            color: 'primary.dark',
                            borderColor: 'transparent',
                            backgroundColor: 'transparent',
                          }}
                          disabled={isLoading || isSendToElationDisabled}
                        />
                      </Button>
                    </Grid>
                    <Grid size={12} sx={{ mb: 1 }}>
                      <Grid size={12} sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: '500' }}>Referral body</Typography>
                      </Grid>
                      <TextField
                        required
                        disabled={isDisabled || getElationTemplate.isPending}
                        multiline
                        fullWidth
                        label="Referral body"
                        name="referralBody"
                        variant="standard"
                        onChange={formik.handleChange}
                        value={formik.values.referralBody}
                      />
                    </Grid>
                  </>
                ) : null}
              </>
            ) : null}
          </BackgroundBlock>
        )}
        <Grid
          size={12}
          sx={{
            mt: 2,
            pb: shouldRenderSaveAndSendToElationButton || shouldRenderSaveButton ? 2 : 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {shouldRenderSaveAndSendToElationButton ? (
              <>
                <Button
                  variant="outlined"
                  type="submit"
                  name="sendToElation"
                  onClick={() => {
                    void formik.setFieldValue('sendToElation', true)
                    void formik.setFieldTouched('sendToElation')
                  }}
                  disabled={isLoading || isSendToElationDisabled}
                  endIcon={
                    editSteerage.isPending ? <CircularProgress size="1em" color="inherit" /> : null
                  }
                >
                  Save and Send to Elation
                </Button>
              </>
            ) : null}
            {shouldRenderSaveButton ? (
              <>
                <Button
                  variant="outlined"
                  type="submit"
                  name="saveReferral"
                  disabled={isLoading || isSaveButtonDisabled}
                  endIcon={
                    editSteerage.isPending ? <CircularProgress size="1em" color="inherit" /> : null
                  }
                >
                  Save
                </Button>
              </>
            ) : null}
          </Box>
        </Grid>
      </form>
    </Box>
  )
}

export const BackgroundBlock: React.FC<BoxProps> = props => {
  const { children, ...rest } = props
  return (
    <Box
      {...rest}
      sx={{
        mx: -2,
        my: 2,
        p: 2,
        // Literal: sx numbers multiply theme.shape.borderRadius (24px, the
        // theme's 12px shape).
        borderRadius: '8px',
        border: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      {children}
    </Box>
  )
}
